import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import UpdateChecker from "@/components/UpdateChecker";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DataProvider } from "@/context/DataContext";
import { StructureProvider } from "@/context/StructureContext";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import Overview from "@/pages/Overview";
import Channels from "@/pages/Channels";
import Roles from "@/pages/Roles";
import EmbedBuilder from "@/pages/EmbedBuilder";
import Members from "@/pages/Members";
import Audit from "@/pages/Audit";
import Settings from "@/pages/Settings";
import Admissions from "@/pages/Admissions";
import Statistics from "@/pages/Statistics";
import Backups from "@/pages/Backups";
import Maintenance from "@/pages/Maintenance";
import Automations from "@/pages/Automations";
import Moderation from "@/pages/Moderation";
import TicketsPage from "@/pages/tickets/TicketsPage";
import VerificationPage from "@/pages/verification/VerificationPage";
import Commands from "@/pages/Commands";
import Levels, { Economy } from "@/pages/Levels";
import { Giveaways, Suggestions, CommunityExtras } from "@/pages/Community";
import { RolesVerification, Responses, SmartChannels, Feeds } from "@/pages/Advanced";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="flex h-screen items-center justify-center bg-[#0B0C10]"><Loader2 className="h-8 w-8 animate-spin text-[#5865F2]" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <DataProvider><StructureProvider>{children}</StructureProvider></DataProvider>;
}

function Guard({ perm, owner, children }) {
  const { can, isOwner } = useAuth();
  if (owner && !isOwner) return <Navigate to="/" replace />;
  if (perm && !can(perm)) return <Navigate to="/" replace />;
  return children;
}

function LoginRoute() {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function App() {
  return (
    <div className="App">
      <Toaster theme="dark" richColors position="top-right" />
      <UpdateChecker />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<Overview />} />
              <Route path="/canali" element={<Guard perm="channels"><Channels /></Guard>} />
              <Route path="/ruoli" element={<Guard perm="roles"><Roles /></Guard>} />
              <Route path="/membri" element={<Guard perm="members"><Members /></Guard>} />
              <Route path="/embed" element={<Guard perm="embed"><EmbedBuilder /></Guard>} />
              <Route path="/statistiche" element={<Guard perm="statistics"><Statistics /></Guard>} />
              <Route path="/backup" element={<Guard perm="backups"><Backups /></Guard>} />
              <Route path="/manutenzione" element={<Guard perm="roles"><Maintenance /></Guard>} />
              <Route path="/moderazione" element={<Guard perm="members"><Moderation /></Guard>} />
              <Route path="/ticket" element={<Guard perm="settings"><TicketsPage /></Guard>} />
              <Route path="/verifica" element={<Guard perm="settings"><VerificationPage /></Guard>} />
              <Route path="/automazioni" element={<Guard perm="settings"><Automations /></Guard>} />
              <Route path="/comandi" element={<Guard perm="settings"><Commands /></Guard>} />
              <Route path="/risposte" element={<Guard perm="settings"><Responses /></Guard>} />
              <Route path="/ruoli-verifica" element={<Guard perm="settings"><RolesVerification /></Guard>} />
              <Route path="/canali-auto" element={<Guard perm="settings"><SmartChannels /></Guard>} />
              <Route path="/notifiche" element={<Guard perm="settings"><Feeds /></Guard>} />
              <Route path="/livelli" element={<Guard perm="settings"><Levels /></Guard>} />
              <Route path="/economia" element={<Guard perm="settings"><Economy /></Guard>} />
              <Route path="/giveaway" element={<Guard perm="settings"><Giveaways /></Guard>} />
              <Route path="/suggerimenti" element={<Guard perm="settings"><Suggestions /></Guard>} />
              <Route path="/extra" element={<Guard perm="settings"><CommunityExtras /></Guard>} />
              <Route path="/registro" element={<Audit />} />
              <Route path="/ammissioni" element={<Guard owner><Admissions /></Guard>} />
              <Route path="/impostazioni" element={<Guard perm="settings"><Settings /></Guard>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
